import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { CapabilityGate } from '../../../components/CapabilityGate';
import { api, ApiError } from '../../../lib/api-client';
import { useToastStore } from '../../../stores/toast-store';
import { usePlaybookNameMap } from '../hooks/use-playbook-name-map';

const inputStyle = {
  background: 'var(--bg-surface-2)',
  borderColor: 'var(--border-default)',
};

export interface PlaybookManualSwitchModalProps {
  conversationId: string;
  /** Playbook ativo hoje — excluído do picker (o backend rejeita 400 igual). */
  currentPlaybookId: string | null;
  open: boolean;
  onClose: () => void;
}

/**
 * PlaybookManualSwitchModal — S7 PR3 (S6 Condução Governada, Q-J8).
 *
 * Troca manual de playbook pelo atendente comum, SOB a matriz
 * `transitionPolicy` (≠ force-switch do supervisor em `CoAttendantToolsPanel`,
 * que a IGNORA — endpoints DIFERENTES, não confundir). Molde
 * `HandoffInitiateModal.tsx` (picker `<select>` + motivo opcional + submit).
 *
 * Picker alimentado por `usePlaybookNameMap` — `GET /v1/playbooks?status=active`
 * (cap `playbook-read`), NÃO `listAvailablePlaybooks` (cap supervisor-only
 * `force-playbook-switch`, inalcançável pelo atendente comum). Gateia o
 * FETCH pelo próprio `open` (só busca quando o modal abre).
 *
 * 422 (`transition-blocked-by-matrix`/hard-block adversarial/ConflictAlert) →
 * toast honesto — a régua S6 §4.4; NÃO tenta reformular a decisão, só
 * comunica que o caminho é pedir ao supervisor.
 */
export function PlaybookManualSwitchModal({
  conversationId,
  currentPlaybookId,
  open,
  onClose,
}: PlaybookManualSwitchModalProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const { playbooks, isLoading } = usePlaybookNameMap(open);
  const [toPlaybookId, setToPlaybookId] = useState('');
  const [reason, setReason] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  if (!open) return null;

  const options = playbooks.filter((p) => p.id !== currentPlaybookId);

  function handleClose(): void {
    setToPlaybookId('');
    setReason('');
    setFailed(null);
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    if (toPlaybookId === '') return;
    setIsPending(true);
    setFailed(null);
    try {
      const body: { toPlaybookId: string; reason?: string } = { toPlaybookId };
      if (reason.trim() !== '') body.reason = reason.trim();
      await api.manualSwitchPlaybook(conversationId, body);
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      useToastStore.getState().push({
        variant: 'success',
        title: 'Playbook trocado.',
        durationMs: 3500,
      });
      handleClose();
    } catch (err) {
      // 422 da matriz/proteção → toast honesto (régua S6 §4.4). Backend já
      // devolve `message` pronta pro humano; não reformulamos.
      const isBlocked = err instanceof ApiError && err.status === 422;
      const message = isBlocked
        ? 'Transição bloqueada — peça ao supervisor.'
        : err instanceof ApiError
          ? err.message
          : 'Não foi possível trocar o playbook.';
      setFailed(message);
      useToastStore.getState().push({ variant: 'error', title: message, durationMs: 6000 });
    } finally {
      setIsPending(false);
    }
  }

  return (
    <CapabilityGate capability="playbook-manual-switch">
      <div
        role="dialog"
        aria-label="Trocar playbook"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      >
        <div
          className="w-full max-w-lg rounded-lg p-6 shadow-xl"
          style={{ background: 'var(--bg-surface)' }}
        >
          <h2 className="mb-4 text-lg font-semibold">Trocar playbook</h2>
          <p className="mb-3 text-sm" style={{ color: 'var(--fg-2)' }}>
            A troca honra a matriz de transição declarada — pares bloqueados ou proteções ativas
            (adversarial/conflito de interesse) impedem a troca direta.
          </p>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Novo playbook</span>
            <select
              className="w-full rounded border px-2 py-1"
              style={inputStyle}
              value={toPlaybookId}
              onChange={(e): void => setToPlaybookId(e.target.value)}
              disabled={isPending || isLoading}
            >
              <option value="">{isLoading ? 'Carregando…' : 'Selecione um playbook'}</option>
              {options.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Motivo (opcional)</span>
            <input
              className="w-full rounded border px-2 py-1"
              style={inputStyle}
              value={reason}
              onChange={(e): void => setReason(e.target.value)}
              placeholder="Ex.: cliente confirmou que quer renegociar a dívida"
              disabled={isPending}
              maxLength={500}
            />
          </label>

          {failed !== null && (
            <div className="banner banner--danger mb-3" role="alert">
              <div className="banner__body">
                <span className="banner__title">{failed}</span>
              </div>
            </div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm"
              style={{ borderColor: 'var(--border-default)' }}
              onClick={handleClose}
              disabled={isPending}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void handleSubmit()}
              disabled={isPending || toPlaybookId === ''}
            >
              {isPending ? 'Trocando…' : 'Trocar'}
            </button>
          </div>
        </div>
      </div>
    </CapabilityGate>
  );
}
